import { type CSSProperties, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { trpc } from '../trpc'
import { getVisitorId } from '../lib/visitor'
import { isAuthenticated, accountsBaseUrl, ssoAccountSub } from '../lib/sso'
import { handoffUrl, mustTapToOpenApp, freeCampaignEntryUrl, upgradeLinkUrl, continueInAppUrl } from '../lib/handoff'
import { unauthenticatedAction, markSignInAttempted, clearSignInAttempt } from '../lib/resumeGuard'
import { LoadingOverlay } from '@weapps/design'
import { NotAvailable } from './NotAvailable'
import { WorkingScreen } from './WorkingScreen'

// Transient "doing X" label shown on the WorkingScreen while the effect runs (pt-BR).
const objectiveVerb: Record<string, string> = {
  GrantProductAccess: 'Liberando acesso',
  JoinGroup: 'Adicionando você ao grupo…',
  JoinCommunity: 'Juntando você à comunidade…',
}

// Interstitial CTA label for a participation campaign (pt-BR); product campaigns label by mode below.
const ctaLabel: Record<string, string> = {
  GrantProductAccess: 'Liberar meu acesso',
  JoinGroup: 'Entrar no grupo',
  JoinCommunity: 'Entrar na comunidade',
}

const PRODUCT_KINDS = new Set(['GrantProductAccess'])

// Shared CTA styles (design system): teal-accent primary, tokenized ghost secondary — matching the app's
// primary buttons (bg-accent + text-surface-raised adapts contrast per theme).
const btnPrimary = 'block w-full rounded-[14px] py-4 text-center text-[1.0625rem] font-bold text-surface-raised bg-accent transition active:scale-[0.99]'
// ADR 0159 amendment: the full-product action — filled, full width, clearly second to the free path.
const btnSecondary = 'mt-2.5 block w-full rounded-[14px] py-4 text-center text-[1.0625rem] font-bold transition active:scale-[0.99]'
const btnSecondaryStyle = { background: 'var(--srf)', color: 'var(--acc)', border: '2px solid var(--acc)' } as const

type LandingCrop = { x: number; y: number; width: number; height: number }

// Reproduce the admin framing with pure CSS (ADR 0088). The crop is authored at the hero's 16:9
// aspect, so scaling the background so the crop's width fills the box (`100/width`, height auto)
// makes the 16:9 crop region exactly fill the 16:9 box; percentage background-position then places
// the crop origin. No JS measurement needed (unlike admin's arbitrary-box CroppedImage).
function heroCropStyle(url: string, crop: LandingCrop): CSSProperties {
  const w = Math.max(0.01, Math.min(1, crop.width))
  const h = Math.max(0.01, Math.min(1, crop.height))
  const pos = (start: number, size: number) =>
    size >= 1 ? 0 : (Math.max(0, Math.min(1 - size, start)) / (1 - size)) * 100
  return {
    backgroundImage: `url("${url}")`,
    backgroundSize: `${100 / w}% auto`,
    backgroundPosition: `${pos(crop.x, w)}% ${pos(crop.y, h)}%`,
    backgroundRepeat: 'no-repeat',
  }
}

export function LandingScreen({ autoResume = false }: { autoResume?: boolean }) {
  const { code = '' } = useParams<{ code: string }>()
  const visitorId = getVisitorId()
  // ADR 0137: accounts-web hands back `?auth=cancelled` when the person deliberately backed out. That is a
  // CHOICE, not the cookie-jar failure the #485 guard exists for — so we neither auto-proceed nor show the
  // "couldn't sign in automatically" page; we show the campaign again so they can retry or read the preview.
  const cancelledSignIn = new URLSearchParams(window.location.search).get('auth') === 'cancelled'
  const [phase, setPhase] = useState<'landing' | 'working' | 'error' | 'auth-required'>('landing')
  // ADR 0173 §4: on a phone the granted product is opened by a TAP (the only thing an OS hands to an app).
  const [error, setError] = useState<string | null>(null)
  const opened = useRef(false)

  // Dev-only preview: `?mock=1` renders a sample campaign without a backend.
  const mockParam = import.meta.env.DEV ? new URLSearchParams(window.location.search).get('mock') : null
  const mock = mockParam !== null
  const landing = trpc.resolveLanding.useQuery(
    { code },
    { enabled: Boolean(code) && !mock },
  )
  const mockData = mock
    ? {
        status: 'active' as const,
        doc: {
          code,
          campaignId: 'camp_demo',
          tenantId: 'tenant_demo',
          tenantSlug: 'primeira-igreja',
          tenantName: 'Primeira Igreja' as string | undefined,
          landingImageUrl: undefined as string | undefined,
          landingImageCrop: undefined as LandingCrop | undefined,
          productTitle: 'Fundamentos da Fé' as string | undefined,
          productCoverUrl:
            "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='320' height='180'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='%234f46e5'/><stop offset='1' stop-color='%2306b6d4'/></linearGradient></defs><rect width='320' height='180' fill='url(%23g)'/></svg>" as
            | string
            | undefined,
          name: 'Estudo de Domingo — acesso gratuito',
          description:
            'Aponte a câmera e receba já o seu acesso ao estudo desta semana, começando exatamente na lição de hoje.',
          type: 'paid_product' as const,
          mode: { giveSample: mockParam !== 'sell', page: null },
          objective: { kind: 'GrantProductAccess' as const, storeOfferId: 'o', productId: 'p' },
          active: true,
        },
      }
    : undefined
  const open = trpc.open.useMutation()
  const markContinued = trpc.markContinued.useMutation()
  const redeem = trpc.redeem.useMutation()
  // ADR 0159 amendment: is the campaign's offer purchasable right now, and what does this visitor hold?
  // Public read; the SPA sends the SSO token when it has one, so a signed-in visitor gets their standing.
  const standingQ = trpc.getOfferStanding.useQuery({ code }, { enabled: Boolean(code) && !mock, retry: false })
  const standing = mock ? { purchasable: true, upgradeOfferId: 'o', standing: null } : standingQ.data ?? null

  const data = mockData ?? landing.data
  const doc = data?.doc
  const status = data?.status

  // Capture the open exactly once when the campaign resolves (skipped in mock preview).
  useEffect(() => {
    if (doc && !opened.current && !mock) {
      opened.current = true
      open.mutate({ code, visitorId })
    }
  }, [doc, code, visitorId, open, mock])

  async function proceed() {
    if (!doc) return
    markContinued.mutate({ code, visitorId })

    if (!isAuthenticated()) {
      // #485: spend at most ONE automatic sign-in round-trip per code. If we already took it and are
      // still unauthenticated, the session cookie isn't reaching this jar — redirecting again would
      // ping-pong /resume ↔ /signin forever (no user interaction needed to trigger it, since the resume
      // route and AutoForward mode both auto-proceed). Stop and ask for an explicit sign-in instead.
      if (unauthenticatedAction(code) === 'manual') {
        setPhase('auth-required')
        return
      }
      markSignInAttempted(code)
      // Delegate login to accounts-web using the SAME entry grow/store use:
      // /signin?redirect_uri=<returnTo>. After login it returns to the campaign
      // resume point, which re-runs redeem (now authenticated) and hands off.
      const returnTo = `${window.location.origin}/c/${encodeURIComponent(code)}/resume`
      window.location.href = `${accountsBaseUrl()}/signin?redirect_uri=${encodeURIComponent(returnTo)}`
      return
    }

    // Authenticated: the round-trip worked, so release the guard for any future visit.
    clearSignInAttempt(code)
    setPhase('working')
    try {
      const result = await redeem.mutateAsync({ code, visitorId })
      if (result.ok) {
        // The product is handed to app.polux (installed app or web app); a sale goes to the store with the
        // campaign attached (ADR 0161 §5).
        const href = handoffUrl(result.handoff as any, doc.tenantSlug, { campaignCode: doc.type === 'paid_product' ? code : undefined, acct: ssoAccountSub() })
        if (mustTapToOpenApp(result.handoff as any, navigator.userAgent)) {
          // ADR 0177 §2: the sign-in on this phone's browser ends on the Continue-in-App Screen, where the tap happens.
          window.location.href = continueInAppUrl(href)
        } else {
          window.location.href = href
        }
      } else {
        const reason = (result as { reason?: string }).reason
        setError(reason === 'effect_failed' ? 'Algo deu errado. Tente novamente.' : 'Campanha indisponível.')
        setPhase('error')
      }
    } catch {
      setError('Não foi possível concluir. Tente novamente.')
      setPhase('error')
    }
  }

  // Auto-forward mode, or the post-auth resume, proceeds without a click — but NEVER straight after a
  // cancellation, which would walk the person back into the sign-in they just backed out of.
  useEffect(() => {
    if (cancelledSignIn) return
    if (doc && status === 'active' && autoResume && phase === 'landing') {
      void proceed()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, status, autoResume, cancelledSignIn])

  // ADR 0161 §3–4: the TYPE decides the landing. A Free Product Campaign has no interstitial — it lands on
  // its preview page, served by the Grow App's campaign route in the browser (web-only host). The open was
  // captured above, so the funnel keeps its first stage. `/c/` and `/o/` are the same link for every type.
  useEffect(() => {
    if (!doc || status !== 'active' || mock) return
    if (doc.type === 'free_product') window.location.replace(freeCampaignEntryUrl(code, visitorId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, status])

  // A cancellation also releases the one-shot guard: the attempt didn't fail, it was declined, so a later
  // deliberate tap deserves a real attempt rather than the dead-end manual page.
  useEffect(() => {
    if (cancelledSignIn) clearSignInAttempt(code)
  }, [cancelledSignIn, code])

  if (!mock && landing.isLoading) return <Splash label="Carregando…" />
  if (!doc || status === 'not_found') return <NotAvailable reason="not_found" />
  if (status === 'unavailable') return <NotAvailable reason="unavailable" />

  const isProductCampaign = PRODUCT_KINDS.has(doc.objective.kind)

  if (phase === 'working')
    return (
      <WorkingScreen
        label={objectiveVerb[doc.objective.kind] ?? 'Um momento…'}
        coverUrl={isProductCampaign ? doc.productCoverUrl : undefined}
      />
    )
  if (phase === 'error') return <NotAvailable reason="error" message={error ?? undefined} onRetry={() => setPhase('landing')} />

  // #485: the one automatic sign-in round-trip came back without a session, so we stop instead of
  // bouncing to /signin again. The manual button deliberately does NOT clear the guard — a user-initiated
  // retry that also fails lands straight back here rather than buying another automatic hop.
  if (phase === 'auth-required')
    return (
      <div
        className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center"
        style={{ background: 'var(--scr)', paddingTop: 'max(1.5rem, env(safe-area-inset-top))', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
        <div className="max-w-md w-full">
          <h1 className="t-title-xl mb-4" style={{ color: 'var(--txt)' }}>Não foi possível entrar automaticamente</h1>
          <p className="t-body mb-6" style={{ color: 'var(--txt2)' }}>Entre na sua conta para liberar o seu acesso.</p>
          <button
            type="button"
            className={btnPrimary}
            onClick={() => {
              const returnTo = `${window.location.origin}/c/${encodeURIComponent(code)}/resume`
              window.location.href = `${accountsBaseUrl()}/signin?redirect_uri=${encodeURIComponent(returnTo)}`
            }}>
            Entrar
          </button>
        </div>
      </div>
    )

  // Interstitial. For a Product Campaign the campaign name stays the headline (the marketing
  // hook), with the target product shown as a supporting identity chip. The hero image is the
  // author-uploaded landing image when present, else the product cover (precedence A, ADR 0078).
  const heroUrl = doc.landingImageUrl ?? (isProductCampaign ? doc.productCoverUrl : undefined)
  const heroIsCover = !doc.landingImageUrl && isProductCampaign && Boolean(doc.productCoverUrl)
  const showProductChip = isProductCampaign && Boolean(doc.productTitle)
  const authed = isAuthenticated()
  // ADR 0161 §2–3: a Paid Product Campaign's actions follow its MODE and the visitor's standing.
  //   sample on  → "Liberar minha amostra" (sign in → sample) + "Quero o produto completo" (Upgrade Link)
  //   sample off → "Comprar o produto" (Upgrade Link; a sale, nothing granted)
  //   sample holder → "Abrir o produto" + "Desbloquear o produto completo"; full holder → "Abrir o produto"
  // No price here — the store is the one place a price is stated. A free campaign never renders this
  // screen's landing (it is redirected to its preview page on resolve).
  const isPaid = doc.type === 'paid_product'
  const givesSample = isPaid && doc.mode.giveSample
  const holdsProduct = isProductCampaign && (standing?.standing === 'sample' || standing?.standing === 'full')
  const canBuy = isPaid && Boolean(standing?.purchasable && standing?.upgradeOfferId) && standing?.standing !== 'full'
  const buyHref = canBuy ? upgradeLinkUrl(doc.tenantSlug, standing!.upgradeOfferId!, code) : null
  const productHandoff = { app: 'grow' as const, kind: 'product' as const, productId: isProductCampaign ? (doc.objective as { productId: string }).productId : '' }
  const openProductHref = isProductCampaign
    ? (() => {
        const href = handoffUrl(productHandoff, doc.tenantSlug, { acct: ssoAccountSub() })
        return mustTapToOpenApp(productHandoff, navigator.userAgent) ? continueInAppUrl(href) : href
      })()
    : '#'
  const chipLabel = isPaid ? (givesSample ? 'Amostra grátis' : 'Disponível na loja') : 'Acesso gratuito'
  const track = () => markContinued.mutate({ code, visitorId })

  return (
    <div
      className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center"
      style={{ background: 'var(--scr)', paddingTop: 'max(1.5rem, env(safe-area-inset-top))', paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
      <div className="max-w-md w-full">
        {heroUrl &&
          // Apply the author's framing only to the landing image itself; the product-cover fallback
          // has no campaign crop and stays plain object-cover.
          (doc.landingImageUrl && doc.landingImageCrop ? (
            <div
              role="img"
              className="w-full rounded-[18px] mb-6 aspect-video"
              style={{ ...heroCropStyle(heroUrl, doc.landingImageCrop), boxShadow: '0 10px 30px rgba(40,25,10,.18)' }}
            />
          ) : (
            <img
              src={heroUrl}
              alt=""
              className="w-full rounded-[18px] mb-6 object-cover aspect-video"
              style={{ boxShadow: '0 10px 30px rgba(40,25,10,.18)' }}
            />
          ))}
        <h1 className="t-title-xl mb-4" style={{ color: 'var(--txt)' }}>{doc.name}</h1>
        {doc.description && <p className="t-body mb-6" style={{ color: 'var(--txt2)' }}>{doc.description}</p>}

        {showProductChip && (
          <div
            className="flex items-center gap-3 text-left rounded-card p-3 mb-8"
            style={{ border: '1px solid var(--color-line)', background: 'var(--srf)' }}>
            {/* Only show a thumbnail when the hero isn't already the cover — avoid duplicating it. */}
            {!heroIsCover && doc.productCoverUrl && (
              <img
                src={doc.productCoverUrl}
                alt=""
                className="h-12 w-12 rounded-xl object-cover shrink-0"
              />
            )}
            <div className="min-w-0">
              <p className="t-title-sm truncate" style={{ color: 'var(--txt)' }}>{doc.productTitle}</p>
              <p className="t-caption mt-0.5" style={{ color: 'var(--txt2)' }}>{chipLabel}</p>
            </div>
          </div>
        )}

        {holdsProduct ? (
          // A signed-in holder (sample or full) is not asked to claim again — the product is theirs.
          <>
            <a href={openProductHref} className={btnPrimary} onClick={track}>Abrir o produto</a>
            {buyHref && standing?.standing === 'sample' && (
              <a href={buyHref} className={btnSecondary} style={btnSecondaryStyle} onClick={track}>Desbloquear o produto completo</a>
            )}
          </>
        ) : isPaid && !givesSample ? (
          // A sale: the purchase IS the action. Not purchasable right now → the plain store link.
          buyHref
            ? <a href={buyHref} className={btnPrimary} onClick={track}>Comprar o produto</a>
            : <button onClick={proceed} className={btnPrimary}>Ver na loja</button>
        ) : isPaid ? (
          // The sample is the free first step, and it asks for sign-in — that is the capture (ADR 0161 §3).
          <>
            <button onClick={proceed} className={btnPrimary}>{authed ? 'Liberar minha amostra' : 'Entrar e liberar minha amostra'}</button>
            {buyHref && <a href={buyHref} className={btnSecondary} style={btnSecondaryStyle} onClick={track}>Quero o produto completo</a>}
          </>
        ) : (
          <button onClick={proceed} className={btnPrimary}>{ctaLabel[doc.objective.kind] ?? 'Continuar'}</button>
        )}
        <p className="t-caption mt-5" style={{ color: 'var(--txt3)' }}>{doc.tenantName ?? doc.tenantSlug}</p>
      </div>
    </div>
  )
}

// Loading state: the shared breathing-logo overlay (portals into the phone frame), identical to the app.
function Splash({ label }: { label: string }) {
  return <LoadingOverlay label={label} delayMs={0} />
}
